<template>
  <div class="szw-network">

    <!-- Hero -->
    <section class="szw-network-hero">
      <div class="szw-network-hero__bg">
        <svg class="szw-network-hero__bg-shape szw-network-hero__bg-shape--right" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,1000 C300,800 400,1000 700,800 C1000,600 1000,0 1000,0 L0,0 Z" fill="#ffffff" opacity="0.05"/>
        </svg>
        <div class="szw-network-hero__blob szw-network-hero__blob--teal"></div>
        <div class="szw-network-hero__blob szw-network-hero__blob--gold"></div>
      </div>

      <div class="szw-container szw-network-hero__content">
        <div class="tw-flex items-start gap-3">
          <div class="szw-network-hero__icon-wrap">
            <span class="material-symbols-outlined" style="font-size: 28px; color: #071c5b;">hub</span>
          </div>
          <div>
            <div class="tw-flex items-center gap-2">
              <h1 class="szw-network-hero__title">{{ $t('samergo_my_network_title') }}</h1>
              <span v-if="contacts.length" class="szw-network-hero__count">
                {{ contacts.length === 1 ? $t('samergo_my_network_contact_one', { count: 1 }) : $t('samergo_my_network_contacts_many', { count: contacts.length }) }}
              </span>
            </div>
            <p class="szw-network-hero__subtitle">{{ $t('samergo_home_action_network_subtitle') }}</p>
          </div>
          <!-- Info balloon explaining how to add organisations to the social map -->
          <div class="szw-network-hero__add-info" tabindex="0" role="note">
            <span class="material-symbols-outlined szw-network-hero__add-info-icon">info</span>
            <span class="szw-network-hero__add-info-label">{{ $t('samergo_my_network_add_info_label') }}</span>
            <div class="szw-network-hero__add-info-balloon" role="tooltip">
              <span class="material-symbols-outlined szw-network-hero__add-info-balloon-icon">tips_and_updates</span>
              <p class="szw-network-hero__add-info-balloon-text">{{ $t('samergo_my_network_add_info_text') }}</p>
            </div>
          </div>
        </div>

        <!-- Filter tabs -->
        <div v-if="contacts.length" class="szw-network-hero__tabs">
          <button
            class="szw-network-hero__tab"
            :class="{ 'szw-network-hero__tab--active': activeFilter === 'all' }"
            @click="activeFilter = 'all'"
          >{{ $t('samergo_my_network_filter_all') }}
          </button>
          <button
            v-for="claim in myClaims"
            :key="claim.actor_id"
            class="szw-network-hero__tab tw-flex items-center gap-1.5"
            :class="{ 'szw-network-hero__tab--active': activeFilter === claim.actor_id }"
            @click="activeFilter = claim.actor_id"
          >
            <ActorAvatar :actor="claim" :width="16" variant="rounded" class="flex-shrink-0"/>
            {{ claim.name }}
          </button>
        </div>

        <!-- Search -->
        <div v-if="contacts.length" class="szw-network-hero__search-wrapper">
          <div class="szw-network-hero__search-glow"></div>
          <div class="szw-network-hero__search-box">
            <span class="material-symbols-outlined szw-network-hero__search-icon">search</span>
            <input
              v-model="searchQuery"
              class="szw-network-hero__search-input"
              :placeholder="$t('samergo_my_network_search_placeholder')"
              type="text"
            />
            <button v-if="searchQuery" class="szw-network-hero__search-clear" @click="searchQuery = ''">
              <span class="material-symbols-outlined" style="font-size: 18px;">close</span>
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Content -->
    <section class="szw-section">
      <div class="szw-container">

        <!-- Empty state -->
        <div v-if="!contacts.length" class="szw-network__empty">
          <div class="szw-network__empty-icon-wrap">
            <span class="material-symbols-outlined" style="font-size: 48px; color: #071c5b; opacity: 0.35;">group</span>
          </div>
          <h2 class="szw-network__empty-title">{{ $t('samergo_my_network_empty_title') }}</h2>
          <p class="szw-network__empty-body">{{ $t('samergo_my_network_empty_body') }}</p>
          <button class="szw-network__btn szw-network__btn--primary" @click="$router.push('/actors-simplified')">
            <span class="material-symbols-outlined" style="font-size: 18px;">person_search</span>
            {{ $t('samergo_my_network_discover_cta') }}
          </button>
        </div>

        <!-- No filtered results -->
        <div v-else-if="!filteredContacts.length" class="szw-network__empty">
          <div class="szw-network__empty-icon-wrap">
            <span class="material-symbols-outlined" style="font-size: 48px; color: #071c5b; opacity: 0.35;">search_off</span>
          </div>
          <p class="szw-network__empty-body">
            <template v-if="searchQuery">{{ $t('samergo_my_network_no_results_search', { query: searchQuery }) }}</template>
            <template v-else>{{ $t('samergo_my_network_no_results_filter') }}</template>
          </p>
          <button v-if="searchQuery" class="szw-network__btn szw-network__btn--ghost" @click="searchQuery = ''">
            {{ $t('samergo_my_network_clear_search') }}
          </button>
          <button v-else class="szw-network__btn szw-network__btn--ghost" @click="activeFilter = 'all'">
            {{ $t('samergo_my_network_show_all') }}
          </button>
        </div>

        <!-- Contact grid -->
        <div v-else class="szw-network__grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <SamergoNetworkCard
            v-for="contact in filteredContacts"
            :key="contact.elastic_id"
            :contact="contact"
            :my-claims="myClaims"
            @removed=""
          />
        </div>

      </div>
    </section>

    <SimplifiedFooterSamergo/>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import SimplifiedFooterSamergo from '@/components/SimplifiedFooter/SimplifiedFooterSamergo.vue'
  import { ACTION_TYPES } from '@/store/modules/network-contacts.js'
  import { fetchMyClaims } from '@/api/network-contacts'
  import TranslationsMixin from '@/util/TranslationsMixin'
  import SamergoNetworkCard from '@/components/Samergo/SamergoNetworkCard.vue'
  import ActorAvatar from '@/Domain/Actor/Components/ActorAvatar.vue'

  export default defineComponent({
    name: 'StaticHomeSamergoNetwork',
    components: { SamergoNetworkCard, SimplifiedFooterSamergo, ActorAvatar },
    mixins: [TranslationsMixin],
    data() {
      return {
        searchQuery: '',
        myClaims: [] as Array<{ actor_id: number; elastic_id: string; name: string; logo: string | null }>,
        activeFilter: 'all' as string | number,
      }
    },
    async mounted() {
      await this.$store.dispatch(ACTION_TYPES.FETCH)
      this.loadMyClaims()
    },
    computed: {
      contacts() {
        return this.$store.getters.networkContacts
      },
      filteredContacts() {
        let list = this.contacts as any[]

        if (this.activeFilter === 'personal') {
          list = list.filter((c: any) => !c.owner_actor_id)
        } else if (this.activeFilter !== 'all') {
          list = list.filter((c: any) => c.owner_actor_id === this.activeFilter)
        } else {
          // Deduplicate by elastic_id on "Alles" tab
          const seen = new Set<string>()
          list = list.filter((c: any) => {
            if (seen.has(c.elastic_id)) return false
            seen.add(c.elastic_id)
            return true
          })
        }

        if (this.searchQuery.trim()) {
          const q = this.searchQuery.toLowerCase()
          list = list.filter((c: any) =>
            (c.name && c.name.toLowerCase().includes(q)) ||
            (c.notes && c.notes.toLowerCase().includes(q))
          )
        }

        return list
      },
    },
    methods: {
      async loadMyClaims() {
        try {
          this.myClaims = await fetchMyClaims()
        } catch {
          // non-critical
        }
      },
    },
  })
</script>

<style scoped lang="scss">
  $szw-navy: #071c5b;
  $szw-navy-dark: #00082f;
  $szw-green: #006e29;
  $szw-surface: #f8f9fa;
  $szw-surface-low: #f3f4f5;
  $szw-border: #c6c5d2;
  $szw-text: #191c1d;
  $szw-text-muted: #454650;

  .szw-network {
    font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
    background: $szw-surface;
    min-height: 100vh;
    color: $szw-text;
  }

  .szw-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 24px;
  }

  // ── Hero ──────────────────────────────────────────────────────────────────

  .szw-network-hero {
    position: relative;
    background: $szw-navy;
    padding: 64px 0 80px;
    overflow: hidden;
  }

  .szw-network-hero__bg {
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
  }

  .szw-network-hero__bg-shape {
    position: absolute;

    &--right {
      top: 0;
      right: 0;
      width: 60%;
      height: 100%;
    }
  }

  .szw-network-hero__blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);

    &--teal {
      top: -40px;
      right: 10%;
      width: 300px;
      height: 300px;
      background: #006e29;
      opacity: 0.25;
    }

    &--gold {
      bottom: -60px;
      left: 5%;
      width: 240px;
      height: 240px;
      background: #f5c343;
      opacity: 0.15;
    }
  }

  .szw-network-hero__content {
    position: relative;
    z-index: 10;
  }

  .szw-network-hero__icon-wrap {
    width: 56px;
    height: 56px;
    border-radius: 16px;
    background: #7df98f;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .szw-network-hero__title {
    font-size: clamp(1.5rem, 3vw, 2.25rem);
    font-weight: 700;
    color: white;
    line-height: 1.2;
    margin: 0;
    letter-spacing: -0.02em;
  }

  .szw-network-hero__subtitle {
    font-size: 0.95rem;
    color: rgba(219, 234, 254, 0.7);
    margin-top: 4px;
  }

  .szw-network-hero__count {
    display: inline-block;
    background: rgba(255, 255, 255, 0.12);
    color: white;
    font-size: 0.875rem;
    font-weight: 600;
    padding: 6px 18px;
    border-radius: 9999px;
    white-space: nowrap;
  }

  .szw-network-hero__add-info {
    position: relative;
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.18);
    color: white;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: help;
    outline: none;
    flex-shrink: 0;
    transition: background 0.15s;

    &:hover,
    &:focus-visible {
      background: rgba(255, 255, 255, 0.16);
    }
  }

  .szw-network-hero__add-info-icon {
    font-size: 18px;
    color: #7df98f;
  }

  .szw-network-hero__add-info-balloon {
    position: absolute;
    top: calc(100% + 12px);
    right: 0;
    width: 300px;
    max-width: 80vw;
    display: flex;
    gap: 10px;
    text-align: left;
    background: white;
    color: #191c1d;
    border-radius: 12px;
    padding: 14px 16px;
    box-shadow: 0 12px 32px rgba(7, 28, 91, 0.22);
    opacity: 0;
    visibility: hidden;
    transform: translateY(-6px);
    transition: opacity 0.18s ease, transform 0.18s ease, visibility 0.18s;
    z-index: 30;

    // little speech-balloon pointer
    &::before {
      content: '';
      position: absolute;
      top: -6px;
      right: 20px;
      width: 12px;
      height: 12px;
      background: white;
      transform: rotate(45deg);
    }
  }

  .szw-network-hero__add-info:hover .szw-network-hero__add-info-balloon,
  .szw-network-hero__add-info:focus .szw-network-hero__add-info-balloon,
  .szw-network-hero__add-info:focus-within .szw-network-hero__add-info-balloon {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }

  .szw-network-hero__add-info-balloon-icon {
    font-size: 20px;
    color: #f5c343;
    flex-shrink: 0;
  }

  .szw-network-hero__add-info-balloon-text {
    margin: 0;
    font-size: 0.85rem;
    line-height: 1.45;
    font-weight: 500;
  }

  // ── Filter tabs ───────────────────────────────────────────────────────────

  .szw-network-hero__tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 24px;
  }

  .szw-network-hero__tab {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 16px;
    border-radius: 9999px;
    border: 1px solid rgba(255, 255, 255, 0.25);
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.82rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: background 0.15s, color 0.15s, border-color 0.15s;

    &:hover {
      background: rgba(255, 255, 255, 0.18);
      color: white;
    }

    &--active {
      background: white;
      color: $szw-navy;
      border-color: white;
    }
  }

  .szw-network-hero__search-wrapper {
    position: relative;
    margin-top: 32px;
    max-width: 640px;
  }

  .szw-network-hero__search-glow {
    position: absolute;
    inset: -4px;
    background: linear-gradient(to right, #ffdf9a, #7df98f);
    border-radius: 16px;
    filter: blur(8px);
    opacity: 0.25;
  }

  .szw-network-hero__search-box {
    position: relative;
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 14px;
    padding: 6px 6px 6px 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  }

  .szw-network-hero__search-icon {
    padding: 0 12px 0 16px;
    color: $szw-navy;
    flex-shrink: 0;
  }

  .szw-network-hero__search-input {
    flex: 1;
    padding: 10px 0;
    background: transparent;
    border: none;
    outline: none;
    font-size: 0.95rem;
    font-family: inherit;
    color: $szw-text;

    &::placeholder {
      color: #757681;
    }
  }

  .szw-network-hero__search-clear {
    background: $szw-surface-low;
    border: none;
    color: $szw-text-muted;
    cursor: pointer;
    display: flex;
    align-items: center;
    padding: 6px;
    border-radius: 8px;
    margin-right: 4px;
    transition: background 0.15s;

    &:hover {
      background: $szw-border;
    }
  }

  // ── Section ───────────────────────────────────────────────────────────────

  .szw-section {
    padding: 64px 0;
    min-height: calc(100vh - 75px - 52px - 303px);
  }

  // ── Empty state ───────────────────────────────────────────────────────────

  .szw-network__empty {
    text-align: center;
    padding: 80px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  .szw-network__empty-icon-wrap {
    width: 96px;
    height: 96px;
    background: #dde1ff;
    border-radius: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .szw-network__empty-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: $szw-text;
    margin: 0;
  }

  .szw-network__empty-body {
    font-size: 0.95rem;
    color: $szw-text-muted;
    max-width: 400px;
    line-height: 1.6;
    margin: 0;
  }

  .szw-network__btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 28px;
    border-radius: 10px;
    font-size: 0.9rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    border: none;
    margin-top: 8px;
    transition: opacity 0.15s, transform 0.1s;

    &:hover {
      opacity: 0.88;
      transform: translateY(-1px);
    }

    &--primary {
      background: $szw-navy;
      color: white;
    }

    &--ghost {
      background: $szw-surface-low;
      color: $szw-text-muted;
      border: 1px solid $szw-border;
    }
  }

  // ── Contact grid ──────────────────────────────────────────────────────────

  .szw-network__grid {
    // spacing via grid gap
  }

  // ── Contact card ──────────────────────────────────────────────────────────

  .szw-nc-card {
    position: relative;
    background: white;
    border: 1px solid $szw-border;
    border-radius: 16px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    transition: box-shadow 0.2s, transform 0.15s;

    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.09);
      transform: translateY(-1px);
    }
  }

  .szw-nc-card__top {
    // layout via tw-flex
  }

  .szw-nc-card__avatar {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: #dde1ff;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .szw-nc-card__avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .szw-nc-card__name {
    font-size: 0.95rem;
    font-weight: 700;
    color: $szw-text;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;

    // leave room for the floating claim tag pinned to the top-right corner
    &--with-tag {
      padding-right: 96px;
    }
  }

  .szw-nc-card__role {
    font-size: 0.78rem;
    color: $szw-text-muted;
    margin-top: 2px;
  }

  .szw-nc-card__action-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $szw-text-muted;
    transition: background 0.15s, color 0.15s;

    .material-symbols-outlined {
      font-size: 18px;
    }

    &:hover {
      background: $szw-surface-low;
      color: $szw-text;
    }

    &--danger:hover {
      background: #ffdad6;
      color: #ba1a1a;
    }

    &--confirm-delete {
      background: #ba1a1a;
      color: white;

      &:hover {
        background: #93000a;
      }
    }
  }

  // ── Editable fields ───────────────────────────────────────────────────────

  .szw-nc-card__field {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    border-top: 1px solid $szw-border;
    padding-top: 12px;
  }

  .szw-nc-card__field-icon {
    font-size: 16px;
    color: $szw-text-muted;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .szw-nc-card__field-value {
    font-size: 0.875rem;
    color: $szw-text;
    line-height: 1.5;
    cursor: text;
    flex: 1;
    border-radius: 6px;
    padding: 2px 6px;
    margin: -2px -6px;
    transition: background 0.15s;

    &:hover {
      background: $szw-surface-low;
    }

    &--empty {
      color: #94a3b8;
      font-style: italic;
    }

    &--notes {
      white-space: pre-wrap;
      word-break: break-word;
    }
  }

  .szw-nc-card__field-input {
    flex: 1;
    font-size: 0.875rem;
    font-family: inherit;
    color: $szw-text;
    background: $szw-surface-low;
    border: 1.5px solid $szw-navy;
    border-radius: 8px;
    padding: 6px 10px;
    outline: none;
    line-height: 1.5;
    transition: border-color 0.15s;

    &:focus {
      border-color: #4b60b8;
    }

    &--textarea {
      resize: none;
      width: 100%;
    }
  }

  .szw-nc-card__edit-actions {
    display: flex;
    flex-direction: row;
    gap: 4px;
    flex-shrink: 0;
    align-self: flex-start;

    &--col {
      flex-direction: column;
    }
  }

  .szw-nc-card__confirm-btn,
  .szw-nc-card__cancel-btn {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;

    .material-symbols-outlined {
      font-size: 16px;
    }
  }

  .szw-nc-card__confirm-btn {
    background: $szw-navy;
    color: white;

    &:hover {
      background: #4b60b8;
    }
  }

  .szw-nc-card__cancel-btn {
    background: $szw-surface-low;
    color: $szw-text-muted;
    border: 1px solid $szw-border;

    &:hover {
      background: #ffdad6;
      color: #ba1a1a;
      border-color: #ffdad6;
    }
  }

  // ── Contributors section ──────────────────────────────────────────────────

  .szw-nc-contributors {
    border-top: 1px solid $szw-border;
    padding-top: 10px;
  }

  .szw-nc-contributors__toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px 6px;
    margin: -4px -6px;
    border-radius: 8px;
    font-size: 0.82rem;
    font-weight: 600;
    color: $szw-text-muted;
    font-family: inherit;
    width: 100%;
    text-align: left;
    transition: background 0.15s, color 0.15s;

    &:hover {
      background: $szw-surface-low;
      color: $szw-text;
    }

    .material-symbols-outlined {
      font-size: 16px;
    }
  }

  .szw-nc-contributors__toggle-icon {
    color: $szw-navy;
    opacity: 0.6;
  }

  .szw-nc-contributors__count {
    background: $szw-surface-low;
    border-radius: 9999px;
    padding: 1px 7px;
    font-size: 0.75rem;
    color: $szw-text-muted;
  }

  .szw-nc-contributors__chevron {
    margin-left: auto;
    transition: transform 0.2s;

    &--open {
      transform: rotate(180deg);
    }
  }

  .szw-nc-contributors__list {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .szw-nc-contributors__empty {
    font-size: 0.8rem;
    color: #94a3b8;
    font-style: italic;
    margin: 0;
    padding: 4px 0;
  }

  .szw-nc-contributor {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    padding: 10px;
    background: $szw-surface-low;
    border-radius: 10px;
    border: 1px solid $szw-border;

    &--skeleton {
      pointer-events: none;
    }
  }

  .szw-nc-contributor__avatar {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: #dde1ff;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .szw-nc-contributor__avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .szw-nc-contributor__body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .szw-nc-contributor__name {
    font-size: 0.82rem;
    font-weight: 700;
    color: $szw-text;
    margin: 0;
    line-height: 1.3;
  }

  .szw-nc-contributor__function {
    font-size: 0.75rem;
    color: $szw-text-muted;
    margin: 0;
  }

  .szw-nc-contributor__field {
    display: flex;
    align-items: flex-start;
    gap: 6px;
  }

  .szw-nc-contributor__field-icon {
    font-size: 14px;
    color: $szw-text-muted;
    margin-top: 2px;
    flex-shrink: 0;
  }

  @keyframes shimmer-anim {
    0% {
      background-position: -400px 0;
    }
    100% {
      background-position: 400px 0;
    }
  }

  .shimmer-contributor {
    background: linear-gradient(90deg, #e0e2e8 25%, #edeef2 50%, #e0e2e8 75%);
    background-size: 800px 100%;
    animation: shimmer-anim 1.4s infinite linear;
  }

  // ── Card badges & description ─────────────────────────────────────────────

  .szw-nc-card__legend-badge {
    display: inline-block;
    font-size: 0.7rem;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 9999px;
    background: #f3f4f5;
    color: $szw-text-muted;
    border: 1px solid $szw-border;
    white-space: nowrap;
  }

  .szw-nc-card__owner-badge {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 2;
    display: inline-block;
    max-width: 140px;
    font-size: 0.7rem;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 9999px;
    background: #dde1ff;
    color: $szw-navy;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .szw-nc-card__desc {
    font-size: 0.78rem;
    color: $szw-text-muted;
    margin: 4px 0 0;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
</style>

<style lang="scss">
  // ── Transfer/copy popup (unscoped — rendered via Teleport outside component root) ──

  .szw-popup {
    position: fixed;
    z-index: 9999;
    background: white;
    border: 1px solid #e2e4ea;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(7, 28, 91, 0.14), 0 2px 8px rgba(0, 0, 0, 0.06);
    min-width: 220px;
    overflow: hidden;
    font-family: 'Plus Jakarta Sans', Inter, sans-serif;
  }

  .szw-popup__header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 16px 12px;
    border-bottom: 1px solid #f0f1f5;
    background: #f8f9ff;
  }

  .szw-popup__header-icon {
    font-size: 16px;
    color: #071c5b;
    opacity: 0.7;
  }

  .szw-popup__header-label {
    font-size: 0.78rem;
    font-weight: 700;
    color: #071c5b;
    letter-spacing: -0.01em;
  }

  .szw-popup__options {
    padding: 6px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .szw-popup__section-label {
    font-size: 0.68rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 6px 10px 4px;
    margin: 0;
  }

  .szw-popup__divider {
    border: none;
    border-top: 1px solid #f0f1f5;
    margin: 4px 0;
  }

  .szw-popup__option {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 10px;
    border: none;
    background: transparent;
    cursor: pointer;
    width: 100%;
    text-align: left;
    transition: background 0.12s;

    &:hover {
      background: #f3f4f8;
    }

    &:active {
      background: #e8eaf2;
    }
  }

  .szw-popup__avatar {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #dde1ff;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    color: #071c5b;

    .material-symbols-outlined {
      font-size: 17px;
    }
  }

  .szw-popup__avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .szw-popup__option-name {
    font-size: 0.85rem;
    font-weight: 600;
    color: #191c1d;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .szw-popup__copy-icon {
    font-size: 14px;
    color: #94a3b8;
    flex-shrink: 0;
  }

  .szw-popup__empty {
    font-size: 0.8rem;
    color: #94a3b8;
    font-style: italic;
    text-align: center;
    padding: 12px 16px;
    margin: 0;
  }
</style>
